/**
 * Line chart de evolución del saldo bancario.
 *
 * Muestra 2 líneas: saldo según banco (lo que el tesorero ingresa) y
 * saldo según sistema (lo que calcula el backend). La brecha visual
 * entre ambas líneas evidencia las diferencias del histórico.
 *
 * Cada punto es un registro de la tabla saldos_bancarios. No se
 * interpola entre puntos — la línea conecta directo registro a
 * registro.
 */
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { SaldoBancarioRead } from '../../lib/api/saldos-bancarios';
import { formatCLP, formatTickCompacto } from '../../lib/format';

type Rango = '7d' | '30d' | '90d' | 'anio' | 'todo';

const RANGOS: { id: Rango; label: string; dias: number | null }[] = [
  { id: '7d', label: '7 días', dias: 7 },
  { id: '30d', label: '30 días', dias: 30 },
  { id: '90d', label: '90 días', dias: 90 },
  { id: 'anio', label: 'Año en curso', dias: null }, // calculado dinámico
  { id: 'todo', label: 'Todo', dias: null },
];

function filtrar(rango: Rango, historial: SaldoBancarioRead[]): SaldoBancarioRead[] {
  if (rango === 'todo') return historial;
  const hoy = new Date();
  let limite: Date;
  if (rango === 'anio') {
    limite = new Date(hoy.getFullYear(), 0, 1); // 1 enero año actual
  } else {
    const def = RANGOS.find((r) => r.id === rango)!;
    const dias = def.dias ?? 30;
    limite = new Date(hoy);
    limite.setDate(limite.getDate() - dias);
  }
  return historial.filter(
    (s) => new Date(s.fecha_registro + 'T00:00:00') >= limite,
  );
}

function formatTickFecha(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
  });
}

interface ChartRow {
  fecha: string;
  banco: number;
  sistema: number;
}

export interface LineChartSaldosProps {
  historial: SaldoBancarioRead[];
  height?: number;
}

export function LineChartSaldos({
  historial,
  height = 280,
}: LineChartSaldosProps) {
  const [rango, setRango] = useState<Rango>('anio');

  const filtrados = useMemo(() => filtrar(rango, historial), [rango, historial]);

  // Recharts requiere data ordenada ascendente para que la línea
  // se dibuje de izquierda a derecha.
  const rows: ChartRow[] = useMemo(
    () =>
      [...filtrados]
        .sort((a, b) => {
          // Orden ascendente por fecha y, para registros del mismo día,
          // por hora — si no se desempata por hora, dos registros del
          // mismo día quedan en el orden (descendente) que entrega la API
          // y la línea se dibuja al revés.
          const porFecha = a.fecha_registro.localeCompare(b.fecha_registro);
          return porFecha !== 0
            ? porFecha
            : a.hora_registro.localeCompare(b.hora_registro);
        })
        .map((s) => ({
          fecha: s.fecha_registro,
          banco: s.saldo_banco,
          sistema: s.saldo_calculado,
        })),
    [filtrados],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-medium text-text-primary">
          Evolución del saldo
        </div>
        <div className="flex items-center gap-1 text-[11px]">
          {RANGOS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRango(r.id)}
              className={`px-2 py-1 rounded border ${
                rango === r.id
                  ? 'border-orange text-orange'
                  : 'border-white/[0.08] text-text-secondary hover:text-text-primary hover:border-white/[0.16]'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          className="flex items-center justify-center text-sm text-text-muted"
          style={{ height }}
        >
          Sin registros en el rango seleccionado.
        </div>
      ) : rows.length === 1 ? (
        <div
          className="flex flex-col items-center justify-center text-sm text-text-muted gap-1"
          style={{ height }}
        >
          <span>1 solo registro en el rango.</span>
          <span className="text-xs">
            La gráfica necesita al menos 2 puntos para mostrar evolución.
          </span>
        </div>
      ) : (
        <div className="w-full" style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={rows}
              margin={{ top: 8, right: 16, left: -8, bottom: 0 }}
            >
              <CartesianGrid
                stroke="rgba(255,255,255,0.05)"
                strokeDasharray="2 4"
                vertical={false}
              />
              <XAxis
                dataKey="fecha"
                tick={{ fill: '#8a8479', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
                tickFormatter={formatTickFecha}
                minTickGap={20}
              />
              <YAxis
                tick={{ fill: '#8a8479', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatTickCompacto}
                domain={['dataMin - 100000', 'dataMax + 100000']}
              />
              <Tooltip
                cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }}
                contentStyle={{
                  backgroundColor: '#1a1a1d',
                  border: '1px solid #26262a',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#f4ede0',
                }}
                labelFormatter={(label) =>
                  new Date(label + 'T00:00:00').toLocaleDateString('es-CL', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })
                }
                formatter={(v, name) => [formatCLP(Number(v)), String(name)]}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, color: '#c8c2b6' }}
                iconType="circle"
              />
              <Line
                type="monotone"
                dataKey="banco"
                name="Según banco"
                stroke="#7ab8ff"
                strokeWidth={2}
                dot={{ r: 3, fill: '#7ab8ff' }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="sistema"
                name="Según sistema"
                stroke="#8a8479"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={{ r: 2.5, fill: '#8a8479' }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
